// packages/agent-kit/src/useToolEditor.ts
import useSWR, { useSWRConfig } from 'swr';
import { useCallback, useState } from 'react';

import { toolService, isApiError } from '@dopomogai/api-client';
import type { ToolResponse, ToolCreatePayload, ToolUpdatePayload, UUID, ToolCodeResponse } from '@dopomogai/types';
import { AgentKitError } from '../types';

// The data structure representing the complete state of the tool editor form.
export type ToolEditorState = ToolResponse & { code: string };

const TOOLS_LIST_CACHE_KEY = 'dopomogai/tools/list';

/**
 * Fetches both the tool's metadata and its code concurrently.
 */
const toolEditorFetcher = async ([, toolId]: [string, UUID]): Promise<ToolEditorState> => {
  const [detailsRes, codeRes] = await Promise.all([
    toolService.getToolById(toolId),
    toolService.getToolCode(toolId),
  ]);

  if (isApiError(detailsRes)) {
    throw new AgentKitError(`Failed to load tool details for ${toolId}`, detailsRes);
  }
  
  // A failure to load code is not critical; we can show an empty editor.
  const codeContent = isApiError(codeRes) ? '' : codeRes.code;

  return { ...detailsRes, code: codeContent };
};

/**
 * A hook to manage the data and actions for creating or editing a tool.
 * @param toolId The ID of the tool to edit, or null/undefined to create a new one.
 */
export const useToolEditor = (toolId: UUID | null) => {
  const isEditing = !!toolId;
  const cacheKey = isEditing ? [`tool/editor`, toolId] : null;

  const { data: tool, error, isLoading, mutate } = useSWR<ToolEditorState, AgentKitError>(
    cacheKey,
    toolEditorFetcher
  );

  const { mutate: mutateList } = useSWRConfig();
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Saves the tool. Handles both creation and updates.
   * @param payload The complete tool data from the form.
   * @returns The saved or created ToolResponse.
   */
  const saveTool = useCallback(async (payload: ToolEditorState): Promise<ToolResponse> => {
    setIsSaving(true);
    
    // Separate the code from the metadata for API calls
    const { code, ...metadata } = payload;
    
    try {
      if (isEditing && toolId) {
        // --- UPDATE LOGIC ---
        const updatePayload: ToolUpdatePayload = {
          name: metadata.name,
          description: metadata.description,
          enabled: metadata.enabled,
          // Add any other updatable metadata fields here
        };

        const [metaRes, codeRes] = await Promise.all([
          toolService.updateTool(toolId, updatePayload),
          toolService.updateToolCode(toolId, { code })
        ]);

        if (isApiError(metaRes)) throw new AgentKitError('Failed to update tool metadata.', metaRes);
        if (isApiError(codeRes)) throw new AgentKitError('Failed to update tool code.', codeRes);
        
        // On success, re-fetch this editor's data and the main tool list
        mutate();
        mutateList(TOOLS_LIST_CACHE_KEY);
        return metaRes;

      } else {
        // --- CREATE LOGIC ---
        const createPayload: ToolCreatePayload = {
          slug: metadata.slug,
          name: metadata.name,
          description: metadata.description ?? null,
          tool_type: metadata.tool_type,
          enabled: metadata.enabled,
          default_params_schema: metadata.default_params_schema ?? null,
          default_params_ui_schema: metadata.default_params_ui_schema ?? null,
          openapi_spec_url: metadata.openapi_spec_url ?? null,
          api_endpoint_url: metadata.api_endpoint_url ?? null,
          requires_dedicated_agent: metadata.requires_dedicated_agent,
          dedicated_llm_model: metadata.dedicated_llm_model ?? null,
          dedicated_system_prompt: metadata.dedicated_system_prompt ?? null,
          agent_config_schema: metadata.agent_config_schema ?? null,
          code,
        };
        const response = await toolService.createTool(createPayload);
        if (isApiError(response)) throw new AgentKitError('Failed to create tool.', response);

        // On success, invalidate the main tool list to include the new tool
        mutateList(TOOLS_LIST_CACHE_KEY);
        return response;
      }
    } finally {
      setIsSaving(false);
    }
  }, [isEditing, toolId, mutate, mutateList]);

  return {
    /** The combined tool data (details + code). Only available in edit mode. */
    tool,
    /** True if the initial data for an existing tool is being fetched. */
    isLoading,
    /** An error object if fetching fails. */
    error,
    /** True if a save/create operation is in progress. */
    isSaving,
    /** The function to save the tool. */
    saveTool,
  };
};